import * as yup from "yup";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import { getAvailablePriceComponentsAsync } from "./../../../../../services/priceComponentService";
import planService from "./../../../../../services/planService";
import ReactSelectAsync from "./../../../../../components/common/ReactSelectAsync";
import TextboxAppend from "../../../../../components/common/TextboxAppend";
import Toaster from "./../../../../../components/common/Toaster";
import _ from "lodash";

const schema = yup.object({
  rate: yup.string().required("Please enter rate."),
  price_component_id: yup.object().shape({
    label: yup.string().required("Please select component."),
    value: yup.string().required("Please select component."),
  }),
});

const PlanForm = ({
  id,
  accountPlanId,
  plan,
  isLoading,
  setLoading,
  onSuccess,
}) => {
  const [rateSelect, setRateSelect] = useState(null);
  const [rateOption, setRateOption] = useState("amount");
  const [selectedType, setSelectedType] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const info = { ...data };
      info["account_plan_id"] = accountPlanId;
      info["price_component_id"] = data.price_component_id.value;
      info["is_percentage"] = rateOption === "percent";

      if (!_.isEmpty(plan)) {
        await planService.savePlanAsync(plan.plan_id, info);
      } else {
        await planService.assignPlanAsync(id, info);
      }

      Toaster.Success("Information Saved Successfully.");
      onSuccess();
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const options = async (search, loadedOptions, { page }) => {
    const params = {
      search_query: search,
      current_page: page,
      page_size: 10,
      sort_column: { path: "created_on", order: "asc" },
    };

    const response = await getAvailablePriceComponentsAsync(id, params);
    const { result: data, count, total_count } = response;

    const options = data.map((item) => {
      const container = {};
      container["label"] = item.name;
      container["value"] = item.id;
      container["rate"] = item.rate;
      container["is_percentage"] = item.is_percentage;
      container["price_component_type"] = item.price_component_type;

      return container;
    });

    const hasMore = total_count > count;

    return {
      options,
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const handleSelect = (data) => {
    setValue("rate", data.rate);
    clearErrors("rate");
    setSelectedType(data.price_component_type);
  };

  useEffect(() => {
    const renderRate = () => {
      if (selectedType === "Base") setRateOption("amount");

      const content = (
        <select
          value={rateOption}
          className="h-full text-sm py-0 pl-2 pr-8 border-transparent bg-transparent text-gray-400"
          onChange={(e) => setRateOption(e.target.value)}
        >
          <option value="amount">PHP</option>
          <option value="percent">%</option>
        </select>
      );

      setRateSelect(content);
    };

    renderRate();
  }, [rateOption, selectedType]);

  useEffect(() => {
    const populateForm = () => {
      const selected_component = {
        label: plan.name,
        value: plan.price_component_id,
        rate: plan.rate,
        is_percentage: plan.is_percentage,
        price_component_type: plan.price_component_type,
      };

      if (plan.is_percentage) setRateOption("percent");

      setValue("price_component_id", selected_component);
      setValue("rate", plan.rate);
      setSelectedType(plan.price_component_type);
    };

    if (!_.isEmpty(plan)) populateForm();
  }, [plan, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelectAsync
            id="price-component-dropdown"
            name="price_component_id"
            title="Price Component"
            options={options}
            control={control}
            onSelectChange={handleSelect}
            register={register("price_component_id")}
            isDisabled={!_.isEmpty(plan)}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <TextboxAppend
            id="rate-textbox"
            name="rate"
            type="text"
            title="Rate"
            autoComplete="off"
            register={register("rate")}
            errors={errors}
            append={rateSelect}
          />
        </div>
      </div>
      <div className="grid grid-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default PlanForm;
